import React, { useState } from "react";
import { Route, Routes } from "react-router-dom";
import { Box, ThemeProvider } from "@mui/material";
import AppBarComponent from "../components/AppBarComponenet";
import ListadoIngresos from "../layouts/ingress/listadoIngresos";
import CrearIngreso from "../layouts/ingress/createIngress";
import Usuarios from "../layouts/usuarios";
import Inventario from "../layouts/inventory";
import ListadoEgresos from "../layouts/egress";
import CreateEgress from "../layouts/egress/createEgress";
import Perfil from "../layouts/perfil";
import Planes from "../layouts/plans";
import CreateProduct from "../layouts/createProduct";
import Dashboard from "../layouts/dashboard";
import Integrations from "../layouts/integrations/index"
import User from "../layouts/users";
import { Navigate } from "react-router-dom";
import theme from "../theme";
import { SIDEBAR_WIDTH_OPEN, SIDEBAR_WIDTH_COLLAPSED } from "../components/SideBar";
import PlanBanner from "../components/PlanBanner";
import EgressUsageBanner from "../components/EgressUsageBanner";
import ProfileIncompleteBanner from "../components/ProfileIncompleteBanner";
import MfaPendingBanner from "../components/MfaPendingBanner";
import TawkChat from "../components/TawkChat";
import AuthService from "../services/authService";

// Guard de ruta por permiso de sección: si el usuario no tiene acceso al tab,
// lo redirige a /home. Los permisos no deben solo ocultar items del sidebar,
// también tienen que impedir el acceso directo por URL.
const RequireTab = ({ tab, children }) =>
  AuthService.canAccessTab(tab) ? children : <Navigate to="/home" replace />;

// Guard por permiso específico (tab + control). Más fino que RequireTab: lo usan
// las rutas de creación, donde tener acceso a la sección no alcanza —hay que tener
// el control `create`—. El owner pasa siempre. Coherente con el enforcement del
// backend, que devuelve 403 al crear sin permiso.
const RequirePermission = ({ tab, control, children }) =>
  AuthService.isOwner() || AuthService.hasPermission(control, tab) ? (
    children
  ) : (
    <Navigate to="/home" replace />
  );

// Guard solo-owner: para secciones a nivel empresa que un colaborador no debe ver
// (perfil/depósito de la cuenta, integraciones externas). Incluye el acceso por
// URL directa. El super-admin se resuelve como owner (rol "owner").
const RequireOwner = ({ children }) =>
  AuthService.isOwner() ? children : <Navigate to="/home" replace />;


export const DashboardRoutes = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const userId = Number(sessionStorage.getItem("User"));

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const sidebarWidth = sidebarOpen ? SIDEBAR_WIDTH_OPEN : SIDEBAR_WIDTH_COLLAPSED;

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          display: "flex",
          minHeight: "100vh",
        }}
      >
        {/* Chat de soporte (Tawk): solo dentro del área logueada. Se activa al
            completar config/supportConfig.js con la cuenta propia. */}
        <TawkChat />
        <Box
          sx={{
            width: `${sidebarWidth}px`,
            flexShrink: 0,
            transition: "width 0.3s ease",
          }}
        />

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
            position: "relative",
            overflow: "visible",
            minWidth: 0,
            transition: "margin-left 0.3s ease",
          }}
        >
          <AppBarComponent
            onSidebarToggle={handleSidebarToggle}
            sidebarOpen={sidebarOpen}
          />
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              backgroundColor: "#f1f5f9",
              position: "relative",
              overflow: "auto",
              marginTop: "64px",
              height: "calc(100vh - 64px)",
              boxShadow: "inset 0 3px 4px #0000001a",
              transition: "all 0.3s ease",
            }}
          >
            <PlanBanner />
            <EgressUsageBanner />
            <ProfileIncompleteBanner />
            <MfaPendingBanner />
            <Routes>
              <Route path="/home" element={<Dashboard />} />
              <Route
                path="/product-list"
                element={
                  <RequireTab tab="product">
                    <p>listado de productos</p>
                  </RequireTab>
                }
              />
              <Route
                path="/create-ingress"
                element={
                  <RequirePermission tab="movement_in" control="create">
                    <CrearIngreso />
                  </RequirePermission>
                }
              />
              <Route
                path="/ingress-list"
                element={
                  <RequireTab tab="movement_in">
                    <ListadoIngresos />
                  </RequireTab>
                }
              />
              <Route
                path="/confirm-ingress"
                element={
                  <RequireTab tab="movement_in">
                    <p>formulario para confirmar ingresos</p>
                  </RequireTab>
                }
              />
              <Route
                path="/inventory"
                element={
                  <RequireTab tab="product">
                    <Inventario />
                  </RequireTab>
                }
              />
              <Route
                path="/create-product"
                element={
                  <RequirePermission tab="product" control="create">
                    <CreateProduct />
                  </RequirePermission>
                }
              />
              <Route
                path="/create-egress"
                element={
                  <RequirePermission tab="movement_out" control="create">
                    <CreateEgress />
                  </RequirePermission>
                }
              />
              <Route
                path="/egress-list"
                element={
                  <RequireTab tab="movement_out">
                    <ListadoEgresos />
                  </RequireTab>
                }
              />
              <Route
                path="/profile"
                element={
                  <RequireOwner>
                    <Perfil />
                  </RequireOwner>
                }
              />
              <Route
                path="/plans"
                element={
                  <RequireOwner>
                    <Planes />
                  </RequireOwner>
                }
              />
              <Route
                path="/collaborators"
                element={
                  <RequireTab tab="user">
                    <Usuarios />
                  </RequireTab>
                }
              ></Route>
              <Route
                path="/users-panel"
                element={userId === 1 ? <User /> : <Navigate to="/home" replace />}
              />
              <Route
                path="/integrations"
                element={
                  <RequireOwner>
                    <Integrations />
                  </RequireOwner>
                }
              ></Route>
            </Routes>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default DashboardRoutes;